import {
	Operator,
	type Filter,
	type MarriageClaimSort,
	type ObjectSchemaProperty,
	type SendableFilter,
	type SendableMarriageClaimSort,
} from "@/models"
import { reactive } from "vue"
import { ArraySchema, castUnsafe, mapApiType } from "vue-utils"
import { getObjectSchemaProperty, getRequiredObjectSchemaProperty } from "./marriageClaimKeyHelpers"

export function convertToSendableSort(sort: MarriageClaimSort) {
	return reactive<SendableMarriageClaimSort>({
		direction: sort.direction,
		by: sort.by.key,
	})
}

export function convertToSendableFilters(filters: Filter[]): SendableFilter[] {
	return filters.map((filter) => ({ Property: filter.Property.key, Operator: filter.Operator, Value: filter.Value }))
}

export function convertToSendableColumns(columns: ObjectSchemaProperty[]): string[][] {
	return columns.map((column) => column.key)
}

export function convertFromSendableSort(data: string | null): MarriageClaimSort | null {
	if (!data) {
		return null
	}
	try {
		const parsedData = JSON.parse(data) as SendableMarriageClaimSort
		const property = getObjectSchemaProperty(parsedData.by)
		if (!property) {
			console.warn(`Unknown property '${parsedData.by.join(" ")}', ignoring`)
			return null
		}
		return {
			direction: parsedData.direction,
			by: property,
		}
	} catch (error: unknown) {
		console.warn(error)
		return null
	}
}

export function convertFromSendableFilters(data: string | null): Filter[] | null {
	if (!data) {
		return null
	}

	let parsedData: SendableFilter[]
	try {
		parsedData = JSON.parse(data) as SendableFilter[]
	} catch (e) {
		console.warn(e)
		return null
	}

	const convertedFilters: Filter[] = []
	for (const filter of parsedData) {
		const propertyName = filter.Property.join(" ")
		try {
			const property = getObjectSchemaProperty(filter.Property)
			if (!property) {
				console.warn(`Missing filter property '${propertyName}', ignoring`)
				continue
			}
			convertedFilters.push({
				Property: property,
				Operator: filter.Operator,
				Value: loadSchemaValue(property, filter.Operator, filter.Value),
			})
		} catch (error) {
			console.warn(`Failed to load filter property '${propertyName}'`, error)
		}
	}

	return convertedFilters
}

function loadSchemaValue(property: ObjectSchemaProperty, operator: Operator, value: unknown): unknown {
	if (operator === Operator.Exists || operator === Operator.NotExists) {
		return null
	}
	if (operator === Operator.In || operator === Operator.NotIn) {
		const schema = new ArraySchema(property.usedSchema)
		return mapApiType(schema, castUnsafe(value))
	}
	return mapApiType(property.usedSchema, castUnsafe(value))
}

export function convertFromSendableColumns(data: string | null): ObjectSchemaProperty[] | null {
	if (!data) {
		return null
	}
	const parsedData = JSON.parse(data) as string[][]
	const convertedColumns = reactive<ObjectSchemaProperty[]>([])
	parsedData.forEach((column) => {
		try {
			convertedColumns.push(getRequiredObjectSchemaProperty(column))
		} catch (error: unknown) {
			console.warn(error)
		}
	})

	return convertedColumns
}
